// Reusable detail-page primitives for the design-vs-build drill-in batches. Route-agnostic: the caller
// supplies its own back-link node + panels, so this works for any entity ($id) detail page.
import type { ReactNode } from "react";
import { ApiError } from "@sympllex/api-client";
import { useI18n } from "../../lib/i18n";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export type DRow = Record<string, unknown>;
export const dErr = (e: unknown) => (e instanceof ApiError ? `${e.status}` : (e as Error).message);
/** Display a value, dash for empty. */
export const dv = (v: unknown) => (v == null || v === "" ? "—" : String(v));

/** Back-link + header + loading/error gate, then the entity body via children(data). */
export function DetailShell({ back, title, sub, q, children }: {
  back: ReactNode;
  title: string;
  sub?: string;
  q: { isLoading: boolean; error: unknown; data: unknown };
  children: (d: DRow) => ReactNode;
}) {
  const { t } = useI18n();
  const d = q.data as DRow | undefined;
  return (
    <section>
      <div className="mb-3">{back}</div>
      {q.isLoading ? <div className="space-y-3"><Skeleton className="h-9 w-2/3" /><Skeleton className="h-40 w-full" /></div>
        : q.error ? <p className="text-sm text-destructive">{t("crud.failed")} {dErr(q.error)}</p>
        : !d ? <p className="text-sm text-muted-foreground">{t("activity.detail.notFound")}</p>
        : <><PageHeader title={title} {...(sub ? { description: sub } : {})} /><div className="grid gap-4">{children(d)}</div></>}
    </section>
  );
}

/** A card of the entity's own fields (label/value grid). */
export function DetailFields({ items }: { items: Array<[string, unknown]> }) {
  return (
    <Card><CardContent className="grid gap-2 pt-6 sm:grid-cols-2">
      {items.map(([k, v]) => (
        <div key={k} className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">{k}</span>
          <span className="text-sm break-words">{dv(v)}</span>
        </div>
      ))}
    </CardContent></Card>
  );
}

/** A panel listing linked/child rows. `renderRow` lets the caller make rows clickable or plain. */
export function DetailPanel({ title, rows, empty, renderRow }: {
  title: string; rows: DRow[]; empty: string; renderRow: (r: DRow) => ReactNode;
}) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : (
          <ul className="m-0 list-none p-0">
            {rows.map((r, i) => (
              <li key={String(r.id ?? i)} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                {renderRow(r)}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
